import {
  Activity,
  ArrowLeftRight,
  BadgePercent,
  BarChart3,
  Briefcase,
  CheckSquare,
  CreditCard,
  FileSignature,
  FileText,
  FolderKanban,
  HandCoins,
  Handshake,
  LayoutDashboard,
  MessageSquare,
  NotebookPen,
  Plus,
  Settings,
  Target,
  Upload,
  Wallet,
  type LucideIcon,
  Presentation,
  Waypoints,
} from "lucide-react";

// ============================================================
// Config statica della Command Palette (⌘K).
// Le voci rispettano canAccess() con le stesse chiavi modulo
// della Sidebar + flag superAdminOnly dove serve.
// ============================================================

export interface PaletteEntry {
  label: string;
  path: string;
  icon: LucideIcon;
  /** Chiave modulo per canAccess() — stessa convenzione della Sidebar. */
  module: string;
  superAdminOnly?: boolean;
  /** Testo secondario mostrato a destra della riga. */
  hint?: string;
  /** Termini extra inclusi nel match testuale (sinonimi, alias). */
  keywords?: string[];
}

export const NAV_ENTRIES: PaletteEntry[] = [
  { label: "Dashboard", path: "/admin", icon: LayoutDashboard, module: "dashboard", keywords: ["home", "panoramica"] },
  { label: "Obiettivi", path: "/admin/goals", icon: Target, module: "goals", keywords: ["goals", "trofei"] },
  { label: "Tasks", path: "/admin/tasks", icon: CheckSquare, module: "tasks", keywords: ["kanban", "board"] },
  { label: "Richieste", path: "/admin/requests", icon: MessageSquare, module: "requests", keywords: ["clienti", "ticket"] },
  { label: "Attività", path: "/admin/activity", icon: Activity, module: "activity", keywords: ["log", "storico"] },
  { label: "Clienti & Lavori", path: "/admin/clients", icon: Briefcase, module: "clients", keywords: ["progetti", "lavori", "retainer"] },
  { label: "Board", path: "/admin/boards", icon: Presentation, module: "boards", keywords: ["lavagna", "miro", "progetto", "incassato"] },
  { label: "Il Filo Rosso", path: "/admin/filo-rosso", icon: Waypoints, module: "filo", superAdminOnly: true, keywords: ["persone", "contatti", "rete", "referenti", "chi ci ha portato", "fbi", "bacheca", "relazioni"] },
  { label: "Tutti i lavori", path: "/admin/projects", icon: FolderKanban, module: "projects", keywords: ["progetti", "retainer"] },
  { label: "Preventivi", path: "/admin/quotes", icon: FileSignature, module: "quotes", keywords: ["quote", "offerte"] },
  { label: "Referenti", path: "/admin/referrals", icon: Handshake, module: "referrals", keywords: ["partner", "commissioni"] },
  { label: "Listino prezzi", path: "/admin/pricing", icon: BadgePercent, module: "pricing", keywords: ["prezzi", "offerte"] },
  { label: "Finance — Panoramica", path: "/admin/finance", icon: Wallet, module: "finance", keywords: ["soldi", "cassa"] },
  { label: "Finance — Movimenti", path: "/admin/finance/movements", icon: ArrowLeftRight, module: "finance", keywords: ["entrate", "uscite", "fatture"] },
  { label: "Finance — Report", path: "/admin/finance/reports", icon: BarChart3, module: "finance", keywords: ["grafici"] },
  { label: "Finance — Stipendi soci", path: "/admin/finance/salaries", icon: HandCoins, module: "salaries", superAdminOnly: true, keywords: ["paghe"] },
  { label: "Abbonamenti", path: "/admin/subscriptions", icon: CreditCard, module: "subscriptions", superAdminOnly: true, keywords: ["ricorrenti", "canoni"] },
  { label: "Agenda personale", path: "/admin/notes", icon: NotebookPen, module: "notes", superAdminOnly: true, keywords: ["note", "appunti"] },
  { label: "Report clienti", path: "/admin/reports", icon: FileText, module: "reports", keywords: ["pdf", "mensili"] },
  { label: "Impostazioni", path: "/admin/settings", icon: Settings, module: "settings", keywords: ["settings", "profilo"] },
];

// NB: `?new=1` è usato SOLO dove la pagina lo legge già
// (FinanceMovements / FinanceOverview). Per le altre azioni
// si naviga alla pagina e basta.
export const ACTION_ENTRIES: PaletteEntry[] = [
  { label: "Nuova task", path: "/admin/tasks", icon: Plus, module: "tasks", keywords: ["crea", "aggiungi"] },
  { label: "Nuovo preventivo", path: "/admin/quotes", icon: Plus, module: "quotes", keywords: ["crea", "quote"] },
  { label: "Nuovo cliente", path: "/admin/clients", icon: Plus, module: "clients", keywords: ["crea", "aggiungi"] },
  { label: "Nuovo obiettivo", path: "/admin/goals", icon: Plus, module: "goals", keywords: ["crea", "goal"] },
  {
    label: "Nuovo movimento finance",
    path: "/admin/finance/movements?new=1",
    icon: Plus,
    module: "finance",
    hint: "entrata, uscita o fattura",
    keywords: ["crea", "spesa"],
  },
  {
    label: "Importa estratto",
    path: "/admin/finance/movements",
    icon: Upload,
    module: "finance",
    hint: "Revolut CSV",
    keywords: ["import", "revolut", "csv", "banca"],
  },
  { label: "Nuovo abbonamento", path: "/admin/subscriptions", icon: Plus, module: "subscriptions", superAdminOnly: true, keywords: ["crea"] },
  { label: "Nuovo stipendio socio", path: "/admin/finance/salaries", icon: HandCoins, module: "salaries", superAdminOnly: true, keywords: ["paga"] },
];

// ============================================================
// Match testuale (accent-insensitive) usato al posto del
// filtro interno di cmdk, così pagine e dati vivi seguono
// la stessa logica.
// ============================================================

export function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/** True se ogni parola della query compare in almeno uno degli haystack. */
export function matchesQuery(query: string, ...haystacks: Array<string | null | undefined>): boolean {
  const words = normalizeText(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const text = haystacks
    .filter((h): h is string => typeof h === "string" && h.length > 0)
    .map(normalizeText)
    .join(" ");
  return words.every((w) => text.includes(w));
}

export function entryMatches(entry: PaletteEntry, query: string): boolean {
  return matchesQuery(query, entry.label, ...(entry.keywords ?? []));
}
